import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from '@/components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, AlertTriangle } from 'lucide-react';
import { getErrorMessage } from '@/utils/errorUtils';

// Import shared form section components
import {
  LoadAIDocumentUploadSection,
  LoadContainerInfoSection,
  LoadTerminalChassisSection,
  LoadDatesSection,
  LoadFeesSection,
  LoadOptionsRemarksSection,
  LoadCustomerDeliverySection
} from './components';
import CustomerFuzzyMatchDialog from './components/CustomerFuzzyMatchDialog';

// Initial form state
const getInitialFormData = () => ({
  container_number: '',
  shipment_type: 'IMPORT',
  container_size: '',
  ssl_id: '',
  terminal_id: '',
  chassis_type: '',
  chassis_number: '',
  mbl: '',
  pin: '',
  yard_id: '',
  eta: null,
  last_free_date: null,
  lfd_yard: null,
  pickup_date: null,
  delivery_date: null,
  appointment_date: null,
  appointment_time_from: '',
  appointment_time_to: '',
  mty_rdy: null,
  mty_return: null,
  return_location: '',
  customer_id: '',
  customer_name: '',
  address_id: '',
  delivery_address: '',
  delivery_city: '',
  delivery_state: '',
  delivery_zip: '',
  delivery_contact: '',
  delivery_phone: '',
  is_multiple_stop: false,
  secondary_address: '',
  secondary_city: '',
  secondary_state: '',
  secondary_zip: '',
  secondary_contact: '',
  secondary_phone: '',
  additional_client_info: '',
  forwarder_reference: '',
  pier_pass: 0,
  pier_pass_paid: false,
  ctf_charge: 0,
  ctf_paid: false,
  fees_paid_by: '',
  overweight_fee: 0,
  is_overweight: false,
  is_hazmat: false,
  is_important: false,
  remarks: ''
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const AddLoadSheet = ({ open, onOpenChange, masterData, customers = [], onSuccess, onCustomersChange, fullPage = false }) => {
  const { api } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  // iter 264d.15 — fuzzy-match dedup dialog state
  const [fuzzyDialogOpen, setFuzzyDialogOpen] = useState(false);
  const [fuzzyMatches, setFuzzyMatches] = useState([]);
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  
  // Duplicate container warning state
  const [duplicateWarning, setDuplicateWarning] = useState({ show: false, containerNumber: '', existingShipment: null });
  const [duplicateBypassAccepted, setDuplicateBypassAccepted] = useState(false);
  
  // DO Upload state
  const [uploadingDO, setUploadingDO] = useState(false);
  const [uploadedDOFile, setUploadedDOFile] = useState(null);
  const doFileInputRef = useRef(null);
  
  const [newAddress, setNewAddress] = useState({ label: '', address: '', city: '', state: '', zip_code: '', contact_name: '', contact_phone: '', receiving_hours_from: '', receiving_hours_to: '', remark: '', is_default: false });
  const [formData, setFormData] = useState(getInitialFormData());
  const [recentCustomerIds, setRecentCustomerIds] = useState([]);

  // Fetch recent customers
  useEffect(() => {
    if (open) {
      api.get('/shipments/recent-customers').then(res => setRecentCustomerIds(res.data || [])).catch(() => {});
    }
  }, [api, open]);

  // Customer selection effect
  useEffect(() => {
    if (formData.customer_id) {
      const customer = customers.find(c => c.id === formData.customer_id);
      if (customer) {
        setSelectedCustomer(customer);
        setFormData(prev => ({ ...prev, customer_name: customer.name }));
        const defaultAddr = customer.addresses?.find(a => a.is_default);
        if (defaultAddr && !formData.address_id) handleAddressSelect(defaultAddr.id, customer);
      }
    } else {
      setSelectedCustomer(null);
    }
  }, [formData.customer_id, customers]);

  const handleAddressSelect = (addressId, customer = selectedCustomer) => {
    if (!customer) return;
    const address = customer.addresses?.find(a => a.id === addressId);
    if (address) {
      setFormData(prev => ({
        ...prev,
        address_id: addressId,
        delivery_address: address.address,
        delivery_city: address.city,
        delivery_state: address.state,
        delivery_zip: address.zip_code,
        delivery_contact: address.contact_name || '',
        delivery_phone: address.contact_phone || ''
      }));
    }
  };

  // Handle DO document upload for AI extraction
  const handleDOUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().match(/\.(pdf|png|jpg|jpeg)$/)) {
      toast.error('Please upload a PDF, PNG, or JPG file');
      return;
    }

    setUploadingDO(true);
    setUploadedDOFile(file);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const response = await api.post('/ai/extract-do', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.data || response.data?.extracted_data) {
        const extracted = response.data.data || response.data.extracted_data;
        
        // Find matching SSL from masterData by name
        let matchedSslId = '';
        if (extracted.ssl_name && masterData?.ssls) {
          const sslMatch = masterData.ssls.find(ssl => 
            ssl.name?.toLowerCase().includes(extracted.ssl_name.toLowerCase()) ||
            extracted.ssl_name.toLowerCase().includes(ssl.name?.toLowerCase())
          );
          if (sslMatch) matchedSslId = sslMatch.id;
        }
        
        // Find matching terminal from masterData by name
        let matchedTerminalId = '';
        if (extracted.terminal_name && masterData?.terminals) {
          const terminalMatch = masterData.terminals.find(t => 
            t.name?.toLowerCase().includes(extracted.terminal_name.toLowerCase()) ||
            extracted.terminal_name.toLowerCase().includes(t.name?.toLowerCase())
          );
          if (terminalMatch) matchedTerminalId = terminalMatch.id;
        }
        
        // Find matching customer from customers list by name
        let matchedCustomerId = '';
        let matchedCustomer = null;
        const customerNameToFind = extracted.customer_name || extracted.shipper_name;
        
        // First: use backend auto-matched customer if available
        if (extracted.matched_customer_id && customers.length > 0) {
          matchedCustomer = customers.find(c => c.id === extracted.matched_customer_id);
          if (matchedCustomer) {
            matchedCustomerId = matchedCustomer.id;
            setSelectedCustomer(matchedCustomer);
          }
        }
        
        // Fallback: try frontend string matching
        if (!matchedCustomer && customerNameToFind && customers.length > 0) {
          matchedCustomer = customers.find(c => 
            c.name?.toLowerCase() === customerNameToFind.toLowerCase()
          );
          if (!matchedCustomer) {
            matchedCustomer = customers.find(c => 
              c.name?.toLowerCase().includes(customerNameToFind.toLowerCase()) ||
              customerNameToFind.toLowerCase().includes(c.name?.toLowerCase())
            );
          }
          if (matchedCustomer) {
            matchedCustomerId = matchedCustomer.id;
            setSelectedCustomer(matchedCustomer);
          }
        }
        
        // Map container size to match dropdown values
        let mappedSize = extracted.container_size || '';
        if (mappedSize) {
          // Normalize size format (e.g., "40GP" -> "40GP", "40HC" -> "40HC")
          mappedSize = mappedSize.toUpperCase().replace(/[^A-Z0-9]/g, '');
        }
        
        // Find matching address if customer found
        // Find matching address — use backend match first, then frontend fallback
        let matchedAddressId = '';
        if (extracted.matched_address_id && matchedCustomer?.addresses?.length > 0) {
          const backendMatch = matchedCustomer.addresses.find(a => a.id === extracted.matched_address_id);
          if (backendMatch) matchedAddressId = backendMatch.id;
        }
        if (!matchedAddressId && matchedCustomer && matchedCustomer.addresses?.length > 0 && extracted.delivery_address) {
          const addrMatch = matchedCustomer.addresses.find(a => 
            a.address?.toLowerCase().includes(extracted.delivery_address.toLowerCase()) ||
            (a.city?.toLowerCase() === extracted.delivery_city?.toLowerCase() && 
             a.state?.toLowerCase() === extracted.delivery_state?.toLowerCase())
          );
          if (addrMatch) matchedAddressId = addrMatch.id;
        }
        
        setFormData(prev => ({
          ...prev,
          container_number: extracted.container_number || prev.container_number,
          container_size: mappedSize || prev.container_size,
          shipment_type: extracted.document_type || prev.shipment_type,
          ssl_id: matchedSslId || prev.ssl_id,
          terminal_id: matchedTerminalId || prev.terminal_id,
          mbl: extracted.mbl_number || extracted.mbl || prev.mbl,
          pin: extracted.pin || prev.pin,
          eta: extracted.eta || prev.eta,
          delivery_date: extracted.delivery_date || prev.delivery_date,
          last_free_date: extracted.last_free_date || prev.last_free_date,
          customer_id: matchedCustomerId || prev.customer_id,
          customer_name: customerNameToFind || prev.customer_name,
          address_id: matchedAddressId || prev.address_id,
          delivery_address: extracted.delivery_address || extracted.pickup_address || prev.delivery_address,
          delivery_city: extracted.delivery_city || extracted.pickup_city || prev.delivery_city,
          delivery_state: extracted.delivery_state || extracted.pickup_state || prev.delivery_state,
          delivery_zip: extracted.delivery_zip || extracted.pickup_zip || prev.delivery_zip,
          delivery_contact: extracted.delivery_contact || prev.delivery_contact,
          delivery_phone: extracted.delivery_phone || prev.delivery_phone,
          is_overweight: extracted.is_overweight || extracted.overweight_check?.is_overweight || prev.is_overweight,
          remarks: extracted.remarks || extracted.special_instructions || extracted.commodity || prev.remarks
        }));
        
        // Show detailed success message
        const filledFields = [];
        if (extracted.container_number) filledFields.push('Container');
        if (extracted.container_size) filledFields.push('Size');
        if (matchedSslId) filledFields.push('SSL');
        if (matchedTerminalId) filledFields.push('Terminal');
        if (extracted.mbl) filledFields.push('MBL');
        if (matchedCustomerId) filledFields.push('Customer');
        else if (customerNameToFind) filledFields.push('Customer Name (new)');
        if (extracted.delivery_address) filledFields.push('Address');
        
        toast.success(`Document processed! Auto-filled: ${filledFields.join(', ')}`);
      }
    } catch (error) {
      console.error('DO extraction error:', error);
      toast.info('Document uploaded. AI extraction unavailable.');
    } finally {
      setUploadingDO(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData(getInitialFormData());
    setSelectedCustomer(null);
    setShowNewCustomer(false);
    setShowNewAddress(false);
    setNewCustomerName('');
    setNewAddress({ label: '', address: '', city: '', state: '', zip_code: '', contact_name: '', contact_phone: '', receiving_hours_from: '', receiving_hours_to: '', remark: '', is_default: false });
    setUploadedDOFile(null);
  };

  const checkDuplicateContainer = async (containerNumber) => {
    if (!containerNumber) return null;
    try {
      const response = await api.get(`/shipments/check-duplicate?container_number=${encodeURIComponent(containerNumber)}`);
      return response.data?.exists ? response.data.shipment : null;
    } catch (error) {
      console.error('Error checking duplicate:', error);
      return null;
    }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) { toast.error('Please enter a customer name'); return; }

    // iter 264d.15 — fuzzy-match check BEFORE creating. Kills the
    // "SteamLogistics vs Steam Logistics" duplicate-drift bug at the source.
    try {
      const fuzzy = await api.get(
        `/customers/fuzzy-match?name=${encodeURIComponent(newCustomerName.trim())}&threshold=0.85`
      );
      const matches = fuzzy.data?.matches || [];
      if (matches.length > 0) {
        setFuzzyMatches(matches);
        setFuzzyDialogOpen(true);
        return; // Wait for user to Link-or-Create-Anyway.
      }
    } catch (e) {
      // Non-fatal — endpoint may not be reachable; fall through to normal create.
    }
    await _doCreateCustomer(newCustomerName);
  };

  const _doCreateCustomer = async (nameOverride) => {
    try {
      const response = await api.post('/customers', { name: nameOverride || newCustomerName });
      toast.success('Customer created');
      setNewCustomerName('');
      setShowNewCustomer(false);
      setFuzzyDialogOpen(false);
      setFuzzyMatches([]);
      onCustomersChange?.();
      setFormData(prev => ({ ...prev, customer_id: response.data.id, customer_name: response.data.name }));
    } catch (error) {
      toast.error('Failed to create customer');
    }
  };

  // iter 264d.15 — link-to-existing handler from CustomerFuzzyMatchDialog.
  const handleLinkExistingCustomer = (existing) => {
    setFormData(prev => ({ ...prev, customer_id: existing.id, customer_name: existing.name }));
    toast.success(`Linked to existing customer "${existing.name}"`);
    setNewCustomerName('');
    setShowNewCustomer(false);
    setFuzzyDialogOpen(false);
    setFuzzyMatches([]);
    onCustomersChange?.();
  };

  const handleCreateAddress = async () => {
    if (!selectedCustomer) { toast.error('Please select a customer first'); return; }
    if (!newAddress.address || !newAddress.city || !newAddress.state || !newAddress.zip_code) { toast.error('Address, city, state, and ZIP are required'); return; }
    try {
      await api.post(`/customers/${selectedCustomer.id}/addresses`, newAddress);
      toast.success('Address added');
      setNewAddress({ label: '', address: '', city: '', state: '', zip_code: '', contact_name: '', contact_phone: '', receiving_hours_from: '', receiving_hours_to: '', remark: '', is_default: false });
      setShowNewAddress(false);
      onCustomersChange?.();
    } catch (error) {
      toast.error('Failed to add address');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.container_number) { toast.error('Container number is required'); return; }

    if (!duplicateBypassAccepted) {
      const existingShipment = await checkDuplicateContainer(formData.container_number);
      if (existingShipment) {
        setDuplicateWarning({ show: true, containerNumber: formData.container_number, existingShipment });
        return;
      }
    }

    setLoading(true);
    try {
      const payload = { ...formData };
      // Safety net: if any "_time_from" field is set but its companion "_date"
      // is empty, default the date to today. The "today" placeholder in the
      // date picker is otherwise misleading and the row saves with no date.
      const timeDatePairs = [
        ['appointment_time_from', 'appointment_date'],
        ['delivery_time_from', 'delivery_date'],
        ['pickup_time_from', 'pickup_date'],
        ['mty_return_time_from', 'mty_return'],
      ];
      timeDatePairs.forEach(([timeField, dateField]) => {
        if (payload[timeField] && !payload[dateField]) {
          payload[dateField] = new Date();
        }
      });
      // Convert date fields to ISO strings, handling both Date objects and string values
      ['eta', 'last_free_date', 'lfd_yard', 'pickup_date', 'delivery_date', 'appointment_date', 'mty_rdy', 'mty_return'].forEach(field => {
        if (payload[field]) {
          // Check if it's a Date object
          if (payload[field] instanceof Date) {
            payload[field] = payload[field].toISOString();
          } else if (typeof payload[field] === 'string') {
            // If it's already a string, ensure it's a valid ISO format
            // If it's just a date (YYYY-MM-DD), convert to full ISO
            if (/^\d{4}-\d{2}-\d{2}$/.test(payload[field])) {
              payload[field] = new Date(payload[field]).toISOString();
            }
            // Otherwise keep as-is (already ISO string)
          }
        }
      });
      
      const response = await api.post('/shipments', payload);
      const shipmentId = response.data?.id;
      
      if (uploadedDOFile && shipmentId) {
        try {
          const docFormData = new FormData();
          docFormData.append('file', uploadedDOFile);
          // Use __auto__ so the backend also runs AI extraction on the doc and
          // fills any empty fields (chassis, seal, mbl, pin, appt_time) the
          // /ai/extract-do path didn't catch. Pre-existing form values are
          // preserved — backend only fills when the field is empty.
          docFormData.append('document_type', '__auto__');
          const docRes = await api.post(`/shipments/${shipmentId}/documents`, docFormData);
          const autofilled = docRes?.data?.autofilled || {};
          const detectedType = docRes?.data?.document_type || 'DO';
          const filledFields = Object.keys(autofilled).filter(
            (k) => k !== 'terminal_name' || autofilled[k],
          );
          if (filledFields.length > 0) {
            const labels = {
              container_number: 'container',
              job_number: 'job #',
              terminal_id: 'terminal',
              terminal: 'terminal',
              terminal_name: 'terminal',
              chassis_number: 'chassis',
              pin: 'PIN',
              appointment_date: 'appt',
              mbl: 'MBL',
              seal_number: 'seal',
            };
            const friendly = Array.from(
              new Set(filledFields.map((k) => labels[k] || k)),
            );
            toast.success(
              `Document saved as ${detectedType}. AI filled: ${friendly.join(', ')}`,
              { duration: 5000 },
            );
          } else {
            toast.success(`Document saved as ${detectedType}`);
          }
        } catch (docError) {
          toast.warning('Shipment created but failed to save DO document');
        }
      }
      
      onSuccess(shipmentId);
      resetForm();
      setDuplicateBypassAccepted(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create load'));
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateBypass = () => {
    setDuplicateBypassAccepted(true);
    setDuplicateWarning({ show: false, containerNumber: '', existingShipment: null });
    setTimeout(() => {
      document.getElementById('add-load-form')?.requestSubmit();
    }, 100);
  };

  // Form content using shared section components
  const formContent = (
    <form id="add-load-form" onSubmit={handleSubmit} className="space-y-6">
      <LoadAIDocumentUploadSection 
        uploadingDO={uploadingDO} 
        uploadedDOFile={uploadedDOFile} 
        setUploadedDOFile={setUploadedDOFile} 
        doFileInputRef={doFileInputRef} 
        handleDOUpload={handleDOUpload}
        title={t('modals.addLoad.uploadDO')}
        description={t('modals.addLoad.uploadDODesc')}
      />
      <LoadContainerInfoSection formData={formData} handleChange={handleChange} masterData={masterData} t={t} />
      <LoadTerminalChassisSection formData={formData} handleChange={handleChange} masterData={masterData} />
      <LoadDatesSection formData={formData} handleChange={handleChange} masterData={masterData} />
      <LoadCustomerDeliverySection 
        formData={formData} 
        handleChange={handleChange} 
        selectedCustomer={selectedCustomer} 
        customers={customers}
        recentCustomerIds={recentCustomerIds}
        showNewCustomer={showNewCustomer} 
        setShowNewCustomer={setShowNewCustomer} 
        newCustomerName={newCustomerName} 
        setNewCustomerName={setNewCustomerName} 
        handleCreateCustomer={handleCreateCustomer}
        showNewAddress={showNewAddress} 
        setShowNewAddress={setShowNewAddress} 
        newAddress={newAddress} 
        setNewAddress={setNewAddress} 
        handleCreateAddress={handleCreateAddress} 
        handleAddressSelect={handleAddressSelect}
      />
      <LoadFeesSection formData={formData} handleChange={handleChange} />
      <LoadOptionsRemarksSection formData={formData} handleChange={handleChange} />

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-slate-800">
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 border-slate-700">Cancel</Button>
        <Button type="submit" disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-500" data-testid="save-load-btn">
          {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save Load
        </Button>
      </div>
    </form>
  );

  // Duplicate container warning dialog
  const duplicateWarningDialog = (
    <Dialog open={duplicateWarning.show} onOpenChange={(open) => !open && setDuplicateWarning({ show: false, containerNumber: '', existingShipment: null })}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-amber-400 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Duplicate Container Warning
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Container <span className="text-white font-mono">{duplicateWarning.containerNumber}</span> already exists.
          </DialogDescription>
        </DialogHeader>
        {duplicateWarning.existingShipment && (
          <div className="p-3 bg-slate-800/50 rounded-lg text-sm space-y-1">
            <div className="text-slate-400">Existing shipment:</div>
            <div className="text-white">Status: {duplicateWarning.existingShipment.status}</div>
            {duplicateWarning.existingShipment.customer_name && (
              <div className="text-white">Customer: {duplicateWarning.existingShipment.customer_name}</div>
            )}
          </div>
        )}
        <div className="flex gap-3 pt-2">
          <Button variant="outline" onClick={() => setDuplicateWarning({ show: false, containerNumber: '', existingShipment: null })} className="flex-1 border-slate-700">
            Cancel
          </Button>
          <Button onClick={handleDuplicateBypass} className="flex-1 bg-amber-600 hover:bg-amber-500">
            Create Anyway
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Full page layout
  if (fullPage) {
    return (
      <div className="min-h-screen bg-slate-950 p-6">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-white mb-6">Add New Load</h1>
          {formContent}
        </div>
        {duplicateWarningDialog}
      </div>
    );
  }

  // Sheet layout (side panel)
  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="bg-slate-900 border-slate-800 w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white">{t('modals.addLoad.title')}</SheetTitle>
            <SheetDescription className="text-slate-400">{t('modals.addLoad.description')}</SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            {formContent}
          </div>
        </SheetContent>
      </Sheet>
      {duplicateWarningDialog}
      <CustomerFuzzyMatchDialog
        open={fuzzyDialogOpen}
        onOpenChange={setFuzzyDialogOpen}
        attemptedName={newCustomerName}
        matches={fuzzyMatches}
        onLink={handleLinkExistingCustomer}
        onCreateAnyway={() => _doCreateCustomer(newCustomerName)}
      />
    </>
  );
};

export default AddLoadSheet;
